import { useCallback, useEffect, useRef } from 'react';
import { isEmpty, noop } from 'lodash';
import { useCurrentValueGetter } from 'hooks';
import { AllEntityId } from 'modules/campaigns/config/allEntityId';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useTracerContext } from 'modules/taboola-common-frontend-modules/tracer/TracerProvider';
import { SPAN_SCOPE } from 'modules/taboola-common-frontend-modules/tracer/config/spanScope';
import { useIsTracerPermitted } from 'modules/taboola-common-frontend-modules/tracer/hooks/useIsTracerPermitted';
import { COMPONENT_STATUS } from 'services/constants';
import { getCurrentSpanProcessingTime } from '../../../../taboola-common-frontend-modules/tracer/utils/getCurrentSpanProcessingTime';
import { TRACER_STATUS_CODE } from '../../../../taboola-common-frontend-modules/tracer/utils/tracerStatusCode';
import { TRACER_STATUS_MESSAGE } from '../../../../taboola-common-frontend-modules/tracer/utils/tracerStatusMessage';
import { ENTITY } from '../../../config';
import { useReportEntityType } from '../../../hooks/useReportEntityType';
import { observeGridPainted } from '../utils/gridPainting';

export const SPAN_EVENT_TYPES = {
    GRID_MEASUREMENT: 'GRID_MEASUREMENT',
    GRAPH_MEASUREMENT: 'GRAPH_MEASUREMENT',
};

// REPORT_PROCESSING_TIMOUT should be a bit higher than TIMEOUT_THRESHOLD(CustomSpanMetricProcessor) to let us search
// for timout log rocket sessions
const REPORT_PROCESSING_TIMOUT = 60 * 1000;
const emptyFilters = [];

export const useReportTracer = ({
    graphDataStatus,
    reportDataStatus,
    reportData,
    graphData,
    selectedReport,
    activeReportFilters = emptyFilters,
    datePreset,
    selectedEntityIds,
}) => {
    const isTracerPermitted = useIsTracerPermitted();
    const [entityType] = useReportEntityType();
    const reportTraceTimeoutIdRef = useRef();
    const gridPainterUnlistenRef = useRef(noop);
    const coupleBackendTrace = useConfigMatch({ [FEATURE_FLAGS.COUPLE_REPORT_UI_AND_BACKEND_TRACE]: 'true' });
    const {
        createRootSpan,
        getRootSpan,
        endRootSpan,
        startSpan,
        getSpan,
        endSpan,
        setSpanStatus,
        isEmptyRootSpan,
        getTraceRequestHeader,
    } = useTracerContext();
    const selectedReportGetter = useCurrentValueGetter(selectedReport);

    const endReportTrace = useCallback(
        (status, message) => {
            clearTimeout(reportTraceTimeoutIdRef.current);
            gridPainterUnlistenRef.current();
            const rootSpan = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);

            if (rootSpan) {
                setSpanStatus(rootSpan, status, message ?? status);
                endRootSpan(SPAN_SCOPE.REPORT_SCOPE);
            }
        },
        [getRootSpan, endRootSpan, setSpanStatus]
    );
    const finalizeReportTrace = useCallback(() => {
        if (isEmptyRootSpan(SPAN_SCOPE.REPORT_SCOPE)) {
            const rootSpan = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);
            endReportTrace(rootSpan?.status?.code || TRACER_STATUS_CODE.OK, rootSpan?.status?.message);
        }
    }, [isEmptyRootSpan, getRootSpan, endReportTrace]);
    const startReportTrace = useCallback(
        ({ traceGrid, traceGraph }) => {
            endReportTrace(TRACER_STATUS_CODE.UNSET);
            if (!traceGrid && !traceGraph) {
                return;
            }

            const rootSpan = createRootSpan(SPAN_SCOPE.REPORT_SCOPE, {
                attributes: {
                    reportId: selectedReportGetter(),
                    dateRange: datePreset,
                    reportFilterValues: activeReportFilters
                        .map(({ type }) => type)
                        .sort()
                        .join(','),
                    filters: isEmpty(activeReportFilters) ? 'NO_FILTERS' : 'ANY_FILTERS',
                    campaignFilter: entityType === ENTITY.CAMPAIGN && selectedEntityIds !== AllEntityId,
                },
            });

            if (traceGrid) {
                const measureProps = {
                    selectedReport: selectedReportGetter(),
                    handler: () => {
                        gridPainterUnlistenRef.current();
                        endSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
                        finalizeReportTrace();
                    },
                };
                startSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT, rootSpan, {
                    selectedReport: selectedReportGetter(),
                });
                gridPainterUnlistenRef.current = observeGridPainted(measureProps);
            }

            if (traceGraph) {
                startSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT, rootSpan, {
                    selectedReport: selectedReportGetter(),
                });
            }

            reportTraceTimeoutIdRef.current = setTimeout(() => {
                endReportTrace(TRACER_STATUS_CODE.ERROR, 'Timeout');
            }, REPORT_PROCESSING_TIMOUT);
        },
        [
            selectedReportGetter,
            datePreset,
            activeReportFilters,
            entityType,
            selectedEntityIds,
            endReportTrace,
            createRootSpan,
            startSpan,
            endSpan,
            finalizeReportTrace,
        ]
    );

    const getReportTraceRequestHeader = useCallback(
        () => getTraceRequestHeader(coupleBackendTrace ? getRootSpan(SPAN_SCOPE.REPORT_SCOPE) : null),
        [getTraceRequestHeader, getRootSpan, coupleBackendTrace]
    );
    const setReportTraceAttributes = useCallback(
        attirbutes => {
            const span = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);
            if (span) {
                span.setAttributes(attirbutes);
            }
        },
        [getRootSpan]
    );

    // Graph end tracing
    useEffect(() => {
        if (!isTracerPermitted) {
            return;
        }

        const span = getSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT);
        if (!span) {
            return;
        }

        if (
            graphDataStatus === COMPONENT_STATUS.ACTIVE ||
            graphDataStatus === COMPONENT_STATUS.ERROR ||
            graphDataStatus === COMPONENT_STATUS.EMPTY
        ) {
            if (graphDataStatus === COMPONENT_STATUS.ERROR) {
                setSpanStatus(span, TRACER_STATUS_CODE.ERROR, `Graph data status: ${graphDataStatus}`);
                setSpanStatus(
                    getRootSpan(SPAN_SCOPE.REPORT_SCOPE),
                    TRACER_STATUS_CODE.ERROR,
                    TRACER_STATUS_MESSAGE.API_ERROR
                );
            } else if (isEmpty(graphData)) {
                setSpanStatus(span, TRACER_STATUS_CODE.OK, 'No available Graph Data');
            }
            endSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT);
            finalizeReportTrace();
        }
    }, [
        endSpan,
        getRootSpan,
        getSpan,
        graphDataStatus,
        selectedReportGetter,
        setSpanStatus,
        startSpan,
        isTracerPermitted,
        graphData,
        finalizeReportTrace,
    ]);

    // Empty grid end tracing
    useEffect(() => {
        if (!isTracerPermitted) {
            return;
        }

        const span = getSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
        if (!span) {
            return;
        }

        if (reportDataStatus === COMPONENT_STATUS.ACTIVE) {
            const rootSpan = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);
            if (rootSpan) {
                rootSpan.setAttributes({ gridProcessingTimeByDataStatus: getCurrentSpanProcessingTime(rootSpan) });
            }
        }

        if (reportDataStatus === COMPONENT_STATUS.ACTIVE && isEmpty(reportData)) {
            setSpanStatus(span, TRACER_STATUS_CODE.OK, 'No available Report Data');
            endSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
            finalizeReportTrace();
        }

        if (reportDataStatus === COMPONENT_STATUS.ERROR || reportDataStatus === COMPONENT_STATUS.EMPTY) {
            setSpanStatus(span, TRACER_STATUS_CODE.ERROR, `Report data status: ${reportDataStatus}`);
            setSpanStatus(
                getRootSpan(SPAN_SCOPE.REPORT_SCOPE),
                TRACER_STATUS_CODE.ERROR,
                TRACER_STATUS_MESSAGE.API_ERROR
            );
            endSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
            finalizeReportTrace();
        }
    }, [
        isTracerPermitted,
        getSpan,
        reportDataStatus,
        reportData,
        setSpanStatus,
        endSpan,
        finalizeReportTrace,
        getRootSpan,
    ]);

    useEffect(
        () => () => {
            endReportTrace(TRACER_STATUS_CODE.UNSET);
        },
        [endReportTrace]
    );

    return { startReportTrace, getReportTraceRequestHeader, setReportTraceAttributes };
};
